import type {
  CareerHistorySurveyStep,
  WorkHistoryEntryDraft,
  WorkHistoryEntryStep,
} from './careerHistorySurveyTypes';
import { WORK_HISTORY_ENTRY_STEPS } from './careerHistorySurveyTypes';
import { buildPneuResumeStep, isPneuStep, pneuStepQuestionKey } from './careerHistorySurveyPneuSteps';

export const WORK_HISTORY_REVIEW_STEP = 'workHistoryReview';

export function stepQuestionKey(step: CareerHistorySurveyStep): string {
  if (isPneuStep(step)) return pneuStepQuestionKey(step);
  return `submitDocuments.careerHistorySurvey.steps.${step}`;
}

export function stepIndex(step: CareerHistorySurveyStep): number {
  if (step === 'workHistoryIntro') return 0;
  if (step === 'recordGate') return 1;
  const idx = WORK_HISTORY_ENTRY_STEPS.indexOf(step as WorkHistoryEntryStep);
  if (idx >= 0) return idx + 2;
  if (step === 'workHistoryReview') return WORK_HISTORY_ENTRY_STEPS.length + 2;
  return -1;
}

export function isEntryStep(step: CareerHistorySurveyStep): step is WorkHistoryEntryStep {
  return WORK_HISTORY_ENTRY_STEPS.includes(step as WorkHistoryEntryStep);
}

export function nextStepAfter(
  current: CareerHistorySurveyStep,
  recordGateAnswer: 'yes' | 'no' | ''
): CareerHistorySurveyStep | null {
  switch (current) {
    case 'workHistoryIntro':
      return 'recordGate';
    case 'recordGate':
      return recordGateAnswer === 'yes' ? 'siteName' : WORK_HISTORY_REVIEW_STEP;
    case 'siteName':
      return 'location';
    case 'location':
      return 'contractor';
    case 'contractor':
      return 'companyName';
    case 'companyName':
      return 'incharge';
    case 'incharge':
      return 'workPerformed';
    case 'workPerformed':
      return 'startDate';
    case 'startDate':
      return 'endDate';
    case 'endDate':
      // Dust duration is derived from start/end; skip the manual ask.
      return 'entrySummary';
    case 'dustDuration':
      return 'entrySummary';
    case 'entrySummary':
      return 'recordGate';
    case 'workHistoryReview':
      return null;
    default:
      return null;
  }
}

/** Last visible field step before summary confirm (for undo-from-summary). */
export function lastFieldStepBeforeSummary(draft: WorkHistoryEntryDraft): WorkHistoryEntryStep {
  for (let i = WORK_HISTORY_ENTRY_STEPS.length - 2; i >= 0; i--) {
    const candidate = WORK_HISTORY_ENTRY_STEPS[i];
    // Dust duration is auto-derived — never a manual step to undo into.
    if (candidate === 'dustDuration') continue;
    if (candidate === 'endDate' && draft.endDateSkipped && !draft.endDate) continue;
    if (isDraftStepAnswered(candidate, draft)) return candidate;
  }
  return 'siteName';
}

export function prevStepForUndo(
  current: CareerHistorySurveyStep,
  draft: WorkHistoryEntryDraft
): CareerHistorySurveyStep | null {
  if (current === 'recordGate') return 'workHistoryIntro';
  if (current === 'siteName') return 'recordGate';
  if (current === 'entrySummary') return lastFieldStepBeforeSummary(draft);
  if (!isEntryStep(current)) return null;

  const idx = WORK_HISTORY_ENTRY_STEPS.indexOf(current);
  if (idx <= 0) return 'recordGate';
  for (let i = idx - 1; i >= 0; i--) {
    const candidate = WORK_HISTORY_ENTRY_STEPS[i];
    if (candidate === 'dustDuration') continue;
    if (candidate === 'endDate' && draft.endDateSkipped && !draft.endDate) continue;
    return candidate;
  }
  return 'recordGate';
}

export function isDraftStepAnswered(step: WorkHistoryEntryStep, draft: WorkHistoryEntryDraft): boolean {
  switch (step) {
    case 'siteName':
      return Boolean(draft.siteName.trim());
    case 'location':
      return Boolean(draft.location.trim());
    case 'contractor':
      return Boolean(draft.contractor.trim());
    case 'companyName':
      return Boolean(draft.companyName.trim());
    case 'incharge':
      return Boolean(draft.incharge.trim());
    case 'workPerformed':
      return Boolean(draft.workPerformed.trim());
    case 'startDate':
      return Boolean(draft.startDate);
    case 'endDate':
      return draft.endDateSkipped || Boolean(draft.endDate);
    case 'dustDuration':
      return (
        draft.dustDurationSkipped ||
        Boolean(draft.dustDuration.trim()) ||
        Boolean(draft.dustDurationYears.trim()) ||
        Boolean(draft.dustDurationMonths.trim())
      );
    case 'entrySummary':
      return true;
    default:
      return false;
  }
}

export function buildResumeStep(
  sectionWorkHistoryDone: boolean,
  sectionPneuDone: boolean,
  entriesCount: number,
  currentStepId: string | null | undefined,
  pneuCheckup: boolean | null | undefined
): CareerHistorySurveyStep {
  if (sectionWorkHistoryDone) {
    return buildPneuResumeStep(sectionWorkHistoryDone, sectionPneuDone, currentStepId, pneuCheckup);
  }
  const step = (currentStepId ?? '').trim() as CareerHistorySurveyStep;
  // Legacy mid-flow: dust duration is no longer asked.
  if (step === 'dustDuration') return 'entrySummary';
  if (step && step !== 'workHistoryIntro') return step;
  if (entriesCount > 0) return 'recordGate';
  return 'workHistoryIntro';
}

/** True when the user is filling a new entry after answering Yes on the record gate (not review edit / pending re-edit). */
export function isNewEntryAfterRecordGateYes(params: {
  editIndex: number | null;
  pendingReEdit: boolean;
  recordGateCommitted: boolean;
  recordGateAnswer: 'yes' | 'no' | '';
}): boolean {
  return (
    params.editIndex === null &&
    !params.pendingReEdit &&
    params.recordGateCommitted &&
    params.recordGateAnswer === 'yes'
  );
}

export function committedStepsForDraft(draft: WorkHistoryEntryDraft): Set<CareerHistorySurveyStep> {
  const set = new Set<CareerHistorySurveyStep>();
  for (const s of WORK_HISTORY_ENTRY_STEPS) {
    if (isDraftStepAnswered(s, draft)) set.add(s);
  }
  return set;
}

export function validateEndAfterStart(draft: WorkHistoryEntryDraft): string | null {
  if (draft.endDateSkipped || !draft.endDate || !draft.startDate) return null;
  if (draft.endDate < draft.startDate) return 'endBeforeStart';
  return null;
}
